import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { UPDATE_PROJECT } from '@/queries/queries';
import { useFlushOnPageHide } from '@/hooks/use-flush-on-page-hide';
import { sanitizeWatermarkPosition, type WatermarkPosition } from '@/lib/watermark/watermarkPosition';

type PendingWrite = { parentId: string; position: WatermarkPosition | null; key: string };

const keyOf = (p: WatermarkPosition | null) => (p ? `${p.x},${p.y}` : 'null');

/**
 * The project/library's free-plan watermark position (null = default) with a
 * debounced writer. Same invariant as every editor writer (see Editor
 * Persistence in CLAUDE.md): a pending write is flushed on unmount and on
 * page hide, never dropped. `updateMyProject` also serves libraries.
 */
export function useWatermarkPosition({
  parentId,
  loadedPosition,
  enabled,
  delay = 400,
}: {
  parentId: string | null | undefined;
  loadedPosition: unknown;
  enabled: boolean;
  delay?: number;
}) {
  const [updateProject] = useMutation(UPDATE_PROJECT);
  const loaded = sanitizeWatermarkPosition(loadedPosition);
  const loadedKey = keyOf(loaded);
  const [position, setPositionState] = useState<WatermarkPosition | null>(loaded);
  const editedRef = useRef(false);
  const lastSavedRef = useRef(loadedKey);
  const pendingRef = useRef<PendingWrite | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seededForRef = useRef(parentId);

  // Adopt the server value until the user edits: the query may resolve after
  // mount (clips pages) or the editor may switch parents.
  useEffect(() => {
    if (seededForRef.current !== parentId) {
      // Flush, never drop: a pending write belongs to the previous parent.
      firePendingRef.current();
      seededForRef.current = parentId;
      editedRef.current = false;
    }
    if (editedRef.current) return;
    lastSavedRef.current = loadedKey;
    setPositionState(sanitizeWatermarkPosition(loadedPosition));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentId, loadedKey]);

  const performSave = useCallback(
    (write: PendingWrite) => {
      // Advance the baseline at fire time: a later edit back to the loaded
      // value (e.g. reset after a drag) must still be written while this
      // request is in flight. A failure restores it so a retry is not skipped.
      const previous = lastSavedRef.current;
      lastSavedRef.current = write.key;
      updateProject({ variables: { id: write.parentId, input: { watermark_position: write.position } } }).catch((err) => {
        if (lastSavedRef.current === write.key) lastSavedRef.current = previous;
        console.error('Failed to persist watermark position', err);
      });
    },
    [updateProject],
  );

  const firePending = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (pending) performSave(pending);
  }, [performSave]);
  useFlushOnPageHide(firePending);
  const firePendingRef = useRef(firePending);
  useEffect(() => {
    firePendingRef.current = firePending;
  }, [firePending]);

  const schedule = useCallback(
    (next: WatermarkPosition | null) => {
      if (!enabled || !parentId) return;
      const key = keyOf(next);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
      if (key === lastSavedRef.current) {
        pendingRef.current = null;
        return;
      }
      pendingRef.current = { parentId, position: next, key };
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        const pending = pendingRef.current;
        pendingRef.current = null;
        if (pending) performSave(pending);
      }, delay);
    },
    [enabled, parentId, delay, performSave],
  );

  const setPosition = useCallback(
    (next: WatermarkPosition) => {
      editedRef.current = true;
      setPositionState(next);
      schedule(next);
    },
    [schedule],
  );

  const resetPosition = useCallback(() => {
    editedRef.current = true;
    setPositionState(null);
    schedule(null);
  }, [schedule]);

  // Unmount: flush, never drop.
  useEffect(() => () => firePendingRef.current(), []);

  return { position, setPosition, resetPosition };
}
